// Timeline manager: RAF-driven enable/disable + opacity tween per element.

import type { ElementPublic } from './api';
import type { SpawnedElement } from './augmentation';

export class TimelineRunner {
  private startTime: number | null = null;
  private paused = false;
  private pauseDelta = 0;
  private pauseStartedAt: number | null = null;

  constructor(
    private readonly elements: SpawnedElement[],
    private readonly totalDurationMs: number,
  ) {}

  /** Called όταν target ανιχνεύεται (first time ή re-detect) */
  start(now: number = performance.now()): void {
    if (this.startTime === null) {
      this.startTime = now;
    } else if (this.paused && this.pauseStartedAt !== null) {
      this.pauseDelta += now - this.pauseStartedAt;
      this.pauseStartedAt = null;
    }
    this.paused = false;
  }

  /** Called όταν target lost */
  pause(now: number = performance.now()): void {
    if (!this.paused) {
      this.paused = true;
      this.pauseStartedAt = now;
      // Hide all spawned roots
      for (const e of this.elements) e.setVisibility(false, 0);
    }
  }

  /** Reset elapsed counter — π.χ. on full augmentation loop */
  reset(): void {
    this.startTime = null;
    this.pauseDelta = 0;
    this.pauseStartedAt = null;
  }

  /** Called per RAF frame */
  tick(now: number = performance.now()): void {
    if (this.paused || this.startTime === null) return;
    let elapsed = now - this.startTime - this.pauseDelta;
    if (elapsed < 0) elapsed = 0;

    // Wrap around once total duration is exceeded (augmentation-level loop).
    if (this.totalDurationMs > 0 && elapsed > this.totalDurationMs) {
      this.startTime = now;
      this.pauseDelta = 0;
      elapsed = 0;
    }

    for (const e of this.elements) {
      const d: ElementPublic = e.data;
      const visible = elapsed >= d.start_time_ms && elapsed <= d.end_time_ms;
      if (!visible) {
        e.setVisibility(false, 0);
        continue;
      }
      const fadeInUntil = d.start_time_ms + d.fade_in_ms;
      const fadeOutFrom = d.end_time_ms - d.fade_out_ms;
      let opacity = 1;
      if (d.fade_in_ms > 0 && elapsed < fadeInUntil) {
        opacity = (elapsed - d.start_time_ms) / d.fade_in_ms;
      } else if (d.fade_out_ms > 0 && elapsed > fadeOutFrom) {
        opacity = (d.end_time_ms - elapsed) / d.fade_out_ms;
      }
      opacity = Math.max(0, Math.min(1, opacity));
      e.setVisibility(true, opacity);
    }
  }
}
